import { useMutation } from "@apollo/client/react"
import { useState } from "react"
import { ReactionPostFragment } from "~/__generated__/graphql"
import { Button } from "~/ui/button"
import { GraphqlError } from "~/ui/graphql-error"
import { CHECK_POST } from "./agent-queries"
import { GENERATE_POST } from "./editorial-queries"

export function AgentReview({
  post,
  body,
  onUse,
  onSaved,
}: {
  post: ReactionPostFragment
  body: string
  onUse: (body: string) => void
  onSaved: () => Promise<void>
}) {
  const [feedback, setFeedback] = useState("")
  const [error, setError] = useState<Error | null>(null)
  const [generate, { loading }] = useMutation(GENERATE_POST)
  const generation = post.latestGeneration
  const busy = loading || generation?.status === "queued" || generation?.status === "writing"
  const included = post.recordingSections.filter((section) => section.included)
  const ready = included.length > 0 && included.every((section) => section.status === "ready")
  const submit = async (revise: boolean) => {
    setError(null)
    try {
      const result = await generate({
        variables: {
          input: {
            postId: post.id,
            clientKey: crypto.randomUUID(),
            feedback: revise ? feedback : undefined,
            previousBody: revise ? (body.trim() ? body : generation?.body) : undefined,
          },
        },
      })
      if (result.error) throw result.error
      setFeedback("")
      await onSaved()
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Could not write the post"))
    }
  }
  return (
    <section
      id="post-writing"
      className="mb-6 mt-7 rounded-xl border bg-muted/30 p-5"
      aria-label="Write from transcripts"
    >
      <h3 className="font-semibold">Shape your story</h3>
      <p className="mt-2 text-sm text-muted-foreground">
        Uses included transcripts, your experience profile, and source context. Each proposal is
        checked with Pangram, with up to two automatic revisions. Your editor stays unchanged until
        you choose a proposal.
      </p>
      <Button className="mt-4" disabled={!ready || busy} onClick={() => void submit(false)}>
        {busy ? "Writing and checking…" : "Write post from transcripts"}
      </Button>
      {!ready && (
        <p className="mt-2 text-sm text-muted-foreground">
          Include a section and wait for its transcript to finish first.
        </p>
      )}
      {generation && (!generation.transcriptCurrent || !generation.profileCurrent) && (
        <p className="mt-3 text-sm text-amber-700">
          Recordings, transcripts, or your profile have changed since this proposal. Write a new
          draft to use the latest saved inputs.
        </p>
      )}
      {generation?.error && (
        <p className="mt-3 text-sm text-destructive" role="alert">
          {generation.error}
        </p>
      )}
      {generation?.status === "ready" && (
        <div className="mt-5 space-y-4">
          <p className="whitespace-pre-wrap text-sm leading-7">{generation.body}</p>
          <p className="text-sm text-muted-foreground">
            {generation.pangramScore != null
              ? `Pangram AI signal: ${Math.round(generation.pangramScore)}%. This describes the proposal above; it is not a verdict on authorship.`
              : (generation.pangramMessage ?? "Pangram score unavailable.")}
          </p>
          <ClaimEvidence claims={generation.claimEvidence} warnings={generation.writingWarnings} />
          <Button variant="outline" onClick={() => onUse(generation.body)}>
            Use this draft in editor
          </Button>
        </div>
      )}
      <div className="mt-6 border-t pt-5">
        <label htmlFor="agent-feedback" className="text-sm font-medium">
          What should change?
        </label>
        <textarea
          id="agent-feedback"
          value={feedback}
          onChange={(event) => setFeedback(event.target.value)}
          maxLength={2000}
          rows={3}
          className="mt-2 w-full rounded-lg border bg-background p-3 text-sm"
          placeholder="For example: lead with the client story, cut the generic opening, and make the last paragraph more practical."
        />
        <p className="mt-2 text-xs text-muted-foreground">
          Revises the text currently in your editor, including unsaved edits. If your editor is
          empty, it revises the latest proposal. Factual additions need support in your saved
          transcripts.
        </p>
        <Button
          className="mt-3"
          variant="outline"
          disabled={
            !ready ||
            busy ||
            !feedback.trim() ||
            !(body.trim() || generation?.body) ||
            body.length > 3000
          }
          onClick={() => void submit(true)}
        >
          Ask agent to revise
        </Button>
      </div>
      {error && (
        <div className="mt-4">
          <GraphqlError error={error} />
        </div>
      )}
      {!!post.generations.length && (
        <details className="mt-5">
          <summary className="cursor-pointer text-sm font-medium">
            Agent conversation & evidence · {post.generations.length}
          </summary>
          <div className="mt-3 space-y-3">
            {post.generations.map((item) => (
              <details key={item.id} className="rounded-lg border bg-background p-3">
                <summary className="cursor-pointer text-sm">
                  {item.feedback || "Initial draft from recordings"} · {item.status}
                </summary>
                <p className="mt-3 whitespace-pre-wrap text-sm leading-6">
                  {item.body || "No proposal yet"}
                </p>
                <ClaimEvidence claims={item.claimEvidence} warnings={item.writingWarnings} />
                {item.body && (
                  <Button
                    className="mt-3"
                    variant="outline"
                    size="sm"
                    onClick={() => onUse(item.body)}
                  >
                    Use this proposal
                  </Button>
                )}
                <p className="mt-3 text-xs text-muted-foreground">
                  {item.profileVersion
                    ? `Profile version ${item.profileVersion}`
                    : "No author profile saved"}{" "}
                  ·{" "}
                  {item.pangramScore == null
                    ? (item.pangramMessage ?? "No score")
                    : `Pangram signal ${Math.round(item.pangramScore)}%`}
                </p>
                <details className="mt-3">
                  <summary className="cursor-pointer text-xs">
                    Saved source, profile, and transcript evidence
                  </summary>
                  <EvidenceDetails evidence={item.evidence} />
                </details>
              </details>
            ))}
          </div>
        </details>
      )}
    </section>
  )
}

export function PangramReview({
  post,
  body,
  onSaved,
}: {
  post: ReactionPostFragment
  body: string
  onSaved: () => Promise<void>
}) {
  const [check, { loading }] = useMutation(CHECK_POST)
  const [error, setError] = useState<Error | null>(null)
  const latest = post.latestCheck
  const generated = post.generations.find((item) => item.body === body && item.status === "ready")
  const exact = latest?.body === body
  const busy = loading || latest?.status === "queued" || latest?.status === "checking"
  const score = exact ? latest.score : generated?.pangramScore
  const message = exact ? latest.message : generated?.pangramMessage
  return (
    <section className="mt-5 rounded-xl border p-4" aria-label="Pangram review">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">Your voice, your decision</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {busy
              ? "Checking the submitted text…"
              : score != null
                ? `Pangram AI signal: ${Math.round(score)}% for this exact text.`
                : exact && message
                  ? message
                  : "This edited text has no current Pangram score."}
          </p>
          {!exact && latest && (
            <p className="mt-1 text-xs text-amber-700">
              The last manual check is stale because the editor text has changed.
            </p>
          )}
          <p className="mt-2 max-w-xl text-xs text-muted-foreground">
            Pangram is a writing signal, not proof of authorship. You can edit, recheck, or approve
            using your own judgment regardless of the score.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={busy || !body.trim() || body.length > 3000}
          onClick={async () => {
            setError(null)
            try {
              const result = await check({
                variables: { input: { postId: post.id, body, clientKey: crypto.randomUUID() } },
              })
              if (result.error) throw result.error
              await onSaved()
            } catch (err) {
              setError(err instanceof Error ? err : new Error("Could not check this post"))
            }
          }}
        >
          Check edited text
        </Button>
      </div>
      {error && (
        <div className="mt-3">
          <GraphqlError error={error} />
        </div>
      )}
    </section>
  )
}

type DraftEvidence = {
  sections?: { id: string; position: number; text: string; version?: number }[]
  profile?: { markdown?: string; version?: number }
  source?: { title?: string; url?: string; summary?: string }
}

function EvidenceDetails({ evidence }: { evidence: DraftEvidence }) {
  return (
    <div className="mt-3 space-y-4 text-sm">
      {evidence.source?.url && (
        <div>
          <p className="font-medium">Source context</p>
          <a
            className="underline underline-offset-4"
            href={evidence.source.url}
            target="_blank"
            rel="noreferrer"
          >
            {evidence.source.title}
          </a>
          <p className="mt-2 whitespace-pre-wrap text-muted-foreground">
            {evidence.source.summary}
          </p>
        </div>
      )}
      {evidence.sections?.map((section) => (
        <div key={section.id}>
          <p className="font-medium">
            Section {section.position} · transcript version {section.version ?? 0}
          </p>
          <p className="mt-1 whitespace-pre-wrap leading-6 text-muted-foreground">{section.text}</p>
        </div>
      ))}
      {evidence.profile?.markdown && (
        <div>
          <p className="font-medium">Author profile used</p>
          <p className="mt-1 whitespace-pre-wrap leading-6 text-muted-foreground">
            {evidence.profile.markdown}
          </p>
        </div>
      )}
    </div>
  )
}

type ClaimCitation = { claim: string; position?: number; quote?: string; verified_quote: boolean }
function ClaimEvidence({ claims, warnings }: { claims: ClaimCitation[]; warnings: string[] }) {
  return (
    <div className="space-y-2">
      {warnings.map((warning) => (
        <p key={warning} className="text-xs text-amber-700">
          {warning}
        </p>
      ))}
      {claims.length > 0 && (
        <details>
          <summary className="cursor-pointer text-sm font-medium">
            Check the claims against your words
          </summary>
          <p className="mt-2 text-xs text-muted-foreground">
            The agent suggested these connections. Quotes are matched exactly to the saved
            transcript; you decide whether they support the claim.
          </p>
          <ul className="mt-3 space-y-3">
            {claims.map((claim, index) => (
              <li key={index} className="rounded-lg border bg-background p-3 text-sm">
                <p>{claim.claim}</p>
                {claim.verified_quote ? (
                  <blockquote className="mt-2 border-l-2 pl-3 text-muted-foreground">
                    Section {claim.position}: “{claim.quote}”
                  </blockquote>
                ) : (
                  <p className="mt-2 text-amber-700">
                    No matching transcript quote. Verify or remove this claim.
                  </p>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  )
}
